import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Link,
  MenuButton,
  MenuButtonButtonProps,
  Tag,
  Text,
  Tooltip,
} from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { VAS_HUB_TRACKING_SOURCE } from '../../pages/VasHub/constants';

const VERTICAL_ELLIPSIS = '⋮';

type ChildType = React.ReactChild | React.ReactFragment | React.ReactPortal;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComparisonArgumentType = string | React.JSXElementConstructor<any>;

const isChildType = (child: ChildType, type: ComparisonArgumentType) =>
  child instanceof Object && 'type' in child && child.type === type;

const findChildOfType = (children: React.ReactNode, type: ComparisonArgumentType) =>
  React.Children.toArray(children).find(child => isChildType(child, type));

const findChildrenOfType = (children: React.ReactNode, type: ComparisonArgumentType) =>
  React.Children.toArray(children).filter(child => isChildType(child, type));

export const ServiceCardSubMenu: React.ComponentType<MenuButtonButtonProps> = MenuButton.Button;

export type ServiceCardSubMenuType = typeof ServiceCardSubMenu;

export interface ServiceCardFooterProps {
  children: React.ReactNode;
}

export function ServiceCardFooter({ children }: ServiceCardFooterProps) {
  return <>{children}</>;
}

export type ServiceCardType = Omit<typeof ServiceCard, 'Footer'>;

export type CardProps = {
  cardKey: string;
  service?: string;
  serviceLabel?: string;
  vendor?: string;
  badge?: string;
  image: string;
  isImageForAvatar?: boolean;
  title?: string;
  isTitleUnderImage?: boolean;
  description: string;
  marketingTemplateLink?: {
    url: string;
    text: string;
  };
  shareLinkButtonName?: string;
  applyButtonName: string;
  applyButtonCTAUrl?: string;
  vendorUrl?: string;
  disabled?: boolean;
  tooltip?: string;
  serviceCardType?: React.ComponentType<CardProps>;
  children?: React.ReactNode;
  pricing?: {
    fullPrice: string;
    discountedPrice: string;
    hasDiscount: boolean;
  } | null;
  isSoldOut?: boolean;
  isLoadingPrice?: boolean;
  priceError?: boolean;
};

export const ServiceCard = ({
  cardKey,
  serviceLabel,
  badge = '',
  image,
  isImageForAvatar = false,
  title,
  isTitleUnderImage = false,
  description,
  marketingTemplateLink,
  tooltip,
  children,
  pricing,
}: CardProps) => {
  const { trackEvent } = useEventTracker();
  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const footerChild = findChildOfType(children, ServiceCardFooter);
  const subMenuChildren = findChildrenOfType(children, ServiceCardSubMenu);
  const [currentUser] = useCurrentUser();

  const onMarketingLinkClick = (rpId = '', card: string) => {
    trackEvent({
      event: `VAS - Vas Hub Page - Marketing Assets Link Clicked`,
      data: {
        rp_id: rpId,
        source: VAS_HUB_TRACKING_SOURCE,
        card: card,
      },
    });
  };

  const rpId = currentUser?.id ?? '';

  return (
    <Card height="fill" key={cardKey}>
      <Flex direction="column" height="fill" justify="start" gap={6} grow={1}>
        <Flex.Item grow={1}>
          <Card.Header>
            <Box pb={4}>
              <Flex justify="between" align="center">
                <Flex align="center" gap={2}>
                  {serviceLabel && (
                    <Tag intent="secondary" size="md">
                      {serviceLabel}
                    </Tag>
                  )}
                  {badge && (
                    <Tag intent="positive" size="md">
                      {badge}
                    </Tag>
                  )}
                </Flex>
                <Flex align="center" gap={2}>
                  {tooltip && (
                    <Tooltip label={tooltip}>
                      <Tooltip.Icon intent="secondary" />
                    </Tooltip>
                  )}
                  {pricing && (
                    <Tag intent="positive" size="md">
                      {pricing.hasDiscount ? (
                        <Flex gap={2} align="center">
                          <s>
                            <Text variant="labelS">{pricing.fullPrice}</Text>
                          </s>
                          <Text variant="labelS" intent="positive">
                            {pricing.discountedPrice}
                          </Text>
                        </Flex>
                      ) : (
                        <Text variant="labelS">{pricing.discountedPrice}</Text>
                      )}
                    </Tag>
                  )}
                  {subMenuChildren?.length > 0 && (
                    <MenuButton
                      alignment="left"
                      menuWidth="hug"
                      open={subMenuOpen}
                      onOpenChange={open => setSubMenuOpen(open)}
                    >
                      <MenuButton.Trigger>
                        <Button
                          emphasis="transparent"
                          intent="secondary"
                          size="md"
                          width="hug"
                          onClick={() =>
                            trackEvent({
                              event:
                                'VAS - Vas Hub Page - Service Card - Vertical Ellipsis Clicked',
                              data: { cardKey },
                            })
                          }
                        >
                          {VERTICAL_ELLIPSIS}
                        </Button>
                      </MenuButton.Trigger>
                      {subMenuChildren}
                    </MenuButton>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Card.Header>

          <Flex.Item>
            <Flex direction="row" gap={4} justify="start" align="center">
              {isImageForAvatar ? (
                <Avatar image={image} alt="Vendor Logo Avatar" />
              ) : (
                <img src={image} height="56px" width="auto" alt="Vendor Logo Img" />
              )}
              {!isTitleUnderImage && title && <Heading level={2}>{title}</Heading>}
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex direction="column" pt={4} gap={4}>
              {isTitleUnderImage && title && (
                <Flex.Item>
                  <Heading level={2}>{title}</Heading>{' '}
                </Flex.Item>
              )}
              <Text contrast="mid">{description}</Text>
              {marketingTemplateLink && (
                <Link
                  href={marketingTemplateLink.url}
                  mode="override_user_choice_and_open_in_new_tab"
                  saliency="default"
                  onClick={() => onMarketingLinkClick(rpId, cardKey)}
                >
                  {marketingTemplateLink.text}
                </Link>
              )}
            </Flex>
          </Flex.Item>
        </Flex.Item>

        <Flex.Item>{footerChild}</Flex.Item>
      </Flex>
    </Card>
  );
};

export default ServiceCard;
